import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Home, Layers, Info, Loader2 } from 'lucide-react';
import {
  AnalyticsStudentRecord,
  TermFilter,
  getClassDisplayName,
  FoundationCompetencyAnalyticsPayload,
} from '../../../services/holisticApi';
import {
  EXPLORER_DOMAINS,
  getDomainCards,
  getCGCards,
  getIndicatorComparisons,
  getClassOptions,
  hasRubricData,
  LEVEL_COLORS,
  DomainCard,
  Distribution,
} from '../data/competencyExplorerUtils';
import {
  mergeCountsForClass,
  hasCountsData,
  getDomainCardsFromCounts,
  getCGCardsFromCounts,
  getIndicatorComparisonsFromCounts,
} from '../data/foundationAnalyticsFromCounts';
import { FOUNDATIONAL_SCHEMA, CurricularGoal } from '../data/foundationalSchema';
import { StackedBPrBar, LevelPill, MovementBadge, BPrBreakdown, EntryExitBars, BPrLegend, CalcInfo } from './foundationShared';

interface CGRow {
  cg: CurricularGoal;
  current?: Distribution;
  movement?: number;
  indicatorCount: number;
}

interface Props {
  records: AnalyticsStudentRecord[];
  term: TermFilter;
  /** Per-competency rubric analytics (national/region/school) when records lack rubric maps. */
  analytics?: FoundationCompetencyAnalyticsPayload;
  /** true at national/region/school — work off the pre-aggregated counts, never iterate records. */
  preferCounts?: boolean;
  /** the analytics query is still in flight. */
  loading?: boolean;
}

export const CompetencyExplorer: React.FC<Props> = ({ records, term, analytics, preferCounts = false, loading = false }) => {
  const [classFilter, setClassFilter] = useState<number | 'all'>('all');
  const [domainId, setDomainId] = useState<string | null>(null);
  const [cgNo, setCgNo] = useState<number | null>(null);

  // Class chips: at higher levels take them from the tiny analytics payload (5 items);
  // only fall back to scanning the large records array at class level.
  const classOptions = useMemo(() => {
    if (preferCounts) {
      return (analytics?.classes ?? [])
        .filter(c => c.total > 0)
        .map(c => ({ classId: c.classId, count: c.total }))
        .sort((a, b) => a.classId - b.classId);
    }
    return getClassOptions(records);
  }, [preferCounts, analytics, records]);

  // Reset a stale class selection (and drill state) if the available classes change,
  // e.g. switching the academic year to one that lacks the currently-selected class.
  useEffect(() => {
    if (classFilter !== 'all' && !classOptions.some(c => c.classId === classFilter)) {
      setClassFilter('all');
      setDomainId(null);
      setCgNo(null);
    }
  }, [classOptions, classFilter]);

  // At higher levels the records carry no rubric maps, so skip the O(records) scan entirely.
  const filtered = useMemo(() => {
    if (preferCounts) return [];
    return classFilter === 'all' ? records : records.filter(r => Number(r.class_id) === classFilter);
  }, [preferCounts, records, classFilter]);

  const rubric = useMemo(() => (preferCounts ? false : hasRubricData(filtered)), [preferCounts, filtered]);

  // Counts path — used at national/region/school where records carry no rubric maps.
  const { merged, total: studentTotal } = useMemo(
    () => mergeCountsForClass(analytics, classFilter),
    [analytics, classFilter],
  );
  const useCounts = useMemo(() => preferCounts && hasCountsData(merged), [preferCounts, merged]);

  const domainCards = useMemo(
    () => (useCounts ? getDomainCardsFromCounts(merged) : getDomainCards(filtered, term)),
    [useCounts, merged, filtered, term],
  );

  const activeDomain = domainId ? EXPLORER_DOMAINS.find(d => d.id === domainId) ?? null : null;
  const activeDomainCard = domainId ? domainCards.find(d => d.domain.id === domainId) ?? null : null;

  const cgCards = useMemo(
    () => {
      if (!activeDomain) return [];
      if (useCounts) return getCGCardsFromCounts(merged, activeDomain);
      return rubric ? getCGCards(filtered, activeDomain, term) : [];
    },
    [activeDomain, useCounts, merged, rubric, filtered, term],
  );

  const activeCG = cgNo ? FOUNDATIONAL_SCHEMA.find(c => c.s_no === cgNo) ?? null : null;
  const indicatorRows = useMemo(
    () => {
      if (!activeCG) return [];
      if (useCounts) return getIndicatorComparisonsFromCounts(merged, activeCG);
      return rubric ? getIndicatorComparisons(filtered, activeCG) : [];
    },
    [activeCG, useCounts, merged, rubric, filtered],
  );

  const hasData = rubric || useCounts;
  const showLoading = loading && !hasData;

  // unified CG rows — clickable at every level; carry live distribution when data exists
  const cgRows: CGRow[] = useMemo(() => {
    if (!activeDomain) return [];
    if (hasData) {
      return cgCards.map(c => ({ cg: c.cg, current: c.current, movement: c.movement, indicatorCount: c.cg.assessment.length }));
    }
    return FOUNDATIONAL_SCHEMA
      .filter(cg => activeDomain.cgNumbers.includes(cg.s_no))
      .map(cg => ({ cg, indicatorCount: cg.assessment.length }));
  }, [activeDomain, hasData, cgCards]);

  const accent = activeDomain?.accent ?? '#6366F1';

  const resetTo = (level: 'root' | 'domain') => {
    if (level === 'root') { setDomainId(null); setCgNo(null); }
    else if (level === 'domain') { setCgNo(null); }
  };

  const selectClass = (c: number | 'all') => { setClassFilter(c); setDomainId(null); setCgNo(null); };

  return (
    <div className="space-y-4">
      {/* ── Class filter + breadcrumb ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-bold uppercase tracking-wide text-gray-600 mr-1">Class</span>
        <button
          onClick={() => selectClass('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
            classFilter === 'all' ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Classes
        </button>
        {classOptions.map(c => (
          <button
            key={c.classId}
            onClick={() => selectClass(c.classId)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              classFilter === c.classId ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {getClassDisplayName(c.classId)}
            <span className="ml-1.5 opacity-60 font-normal">{c.count}</span>
          </button>
        ))}
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="flex items-center gap-1.5 min-w-0">
          <button onClick={() => resetTo('root')} className={`inline-flex items-center gap-1 font-semibold ${domainId ? 'text-indigo-600 hover:text-indigo-800' : 'text-gray-700'}`}>
            <Home size={12} /> Domains
          </button>
          {activeDomain && (
            <>
              <ChevronRight size={13} className="text-gray-500" />
              <button onClick={() => resetTo('domain')} className={`font-semibold ${cgNo ? 'text-indigo-600 hover:text-indigo-800' : 'text-gray-700'}`}>
                {activeDomain.shortLabel}
              </button>
            </>
          )}
          {activeCG && (
            <>
              <ChevronRight size={13} className="text-gray-500" />
              <span className="font-semibold text-gray-700">CG-{activeCG.s_no}</span>
            </>
          )}
        </div>
        <CalcInfo
          align="right"
          title="How Competency analytics is calculated"
          lines={[
            'Each competency is a teacher rubric rating per student: 1 = Beginner, 2 = Progressing, 3 = Proficient.',
            'Domain and Curricular-Goal % = the average rubric across all their competencies, scaled to 0–100% (rating ÷ 3 × 100).',
            'The headline % and bars show the Exit assessment (Term 2). Movement = Exit % − Entry % (Term 1 → Term 2).',
            'Open a Curricular Goal, then a competency, to see the Beginner / Progressing / Proficient split for Entry vs Exit.',
            'Bands: Proficient ≥ 83%, Progressing 50–83%, Beginner < 50%.',
          ]}
        />
      </div>

      {/* ── LEVEL 3: Indicators ───────────────────────────────────────────── */}
      {showLoading ? (
        <ExplorerLoading label="Loading competency analytics…" />
      ) : activeCG ? (
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-indigo-50/50 to-white p-4">
            <div className="flex items-start gap-3">
              <span className="px-2 py-1 rounded-lg text-xs font-bold bg-indigo-600 text-white shrink-0">CG-{activeCG.s_no}</span>
              <div>
                <p className="text-sm font-semibold text-gray-800">{activeCG.curricularGoal}</p>
                <p className="text-[13px] text-gray-600 mt-0.5">{activeDomain?.label} · {indicatorRows.length} competencies · Entry (Term 1) vs Exit (Term 2)</p>
              </div>
            </div>
          </div>

          {hasData ? (
            <div className="space-y-3">
              {indicatorRows.map(row => (
                <div key={row.indicator.entryKey} className="rounded-xl border border-gray-200 p-4 hover:shadow-sm transition">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <span className="px-2 py-0.5 rounded-md text-[12px] font-bold text-white shrink-0 mt-0.5" style={{ background: accent }}>
                        {row.indicator.entryKey.replace(/_/g, '.')}
                      </span>
                      <p className="text-[13px] text-gray-600">{row.indicator.entryLabel}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-gray-600 font-semibold">MOVEMENT</p>
                      <MovementBadge delta={row.movement} size="md" />
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-5">
                    <BPrBreakdown dist={row.entry} title="Entry (Term 1)" />
                    <div className="hidden sm:block w-px bg-gray-100" />
                    <BPrBreakdown dist={row.exit} title="Exit (Term 2)" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <NoRubricNote />
              {activeCG.assessment.map(ind => (
                <div key={ind.entryKey} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
                  <span className="px-2 py-1 rounded-lg text-[13px] font-bold text-white shrink-0" style={{ background: accent }}>
                    {ind.entryKey.replace(/_/g, '.')}
                  </span>
                  <p className="text-[13px] text-gray-600 flex-1 min-w-0">{ind.entryLabel}</p>
                  <span className="text-[11px] font-semibold text-amber-500 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5 shrink-0">
                    class-level data
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeDomain ? (
        /* ── LEVEL 2: Curricular Goals ───────────────────────────────────── */
        <div className="space-y-3">
          {activeDomainCard && (
            <DomainHeader
              card={activeDomainCard}
              studentTotal={useCounts ? studentTotal : activeDomainCard.current.total}
            />
          )}
          {!hasData && <NoRubricNote />}

          <p className="text-[13px] font-semibold text-gray-600 uppercase tracking-wide px-1">
            Curricular Goals · tap to view competencies
          </p>
          <div className="space-y-2">
            {cgRows.map(row => (
              <button
                key={row.cg.s_no}
                onClick={() => setCgNo(row.cg.s_no)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm bg-white transition text-left group"
              >
                <span className="px-2 py-1 rounded-lg text-[13px] font-bold text-white shrink-0" style={{ background: accent }}>
                  CG-{row.cg.s_no}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700 truncate mb-1">{row.cg.curricularGoal}</p>
                  {row.current
                    ? <StackedBPrBar dist={row.current} height={9} />
                    : <p className="text-[12px] text-gray-600">{row.indicatorCount} competencies</p>}
                </div>
                {row.current ? (
                  <div className="text-right shrink-0 w-16">
                    <p className="text-sm font-bold" style={{ color: LEVEL_COLORS[row.current.level] }}>{row.current.avgPct}%</p>
                    <MovementBadge delta={row.movement ?? 0} />
                  </div>
                ) : (
                  <span className="text-[12px] font-semibold text-gray-600 shrink-0">{row.indicatorCount} comp.</span>
                )}
                <ChevronRight size={16} className="text-gray-500 group-hover:text-indigo-400 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* ── LEVEL 1: Domain grid ────────────────────────────────────────── */
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-gray-600 inline-flex items-center gap-1.5">
              <Layers size={13} /> Select a domain to drill into Curricular Goals and competencies
            </p>
            <BPrLegend />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {domainCards.map(card => (
              <button
                key={card.domain.id}
                onClick={() => setDomainId(card.domain.id)}
                disabled={card.current.total === 0}
                className={`text-left rounded-xl border border-gray-200 p-4 transition group bg-gradient-to-br ${card.domain.soft} ${
                  card.current.total === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md hover:border-indigo-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{card.domain.icon}</span>
                    <span className="text-xs font-bold text-gray-700">{card.domain.label}</span>
                  </div>
                  <ChevronRight size={15} className="text-gray-500 group-hover:text-indigo-400" />
                </div>
                <div className="flex items-end justify-between mb-2">
                  <span className="text-2xl font-black" style={{ color: LEVEL_COLORS[card.current.level] }}>{card.current.avgPct}%</span>
                  <LevelPill level={card.current.level} />
                </div>
                <StackedBPrBar dist={card.current} height={10} />
                <div className="flex items-center justify-between mt-2.5 text-[12px] text-gray-600">
                  <span>{(useCounts ? studentTotal : card.current.total).toLocaleString()} students</span>
                  {card.hasEntryExit
                    ? <span className="inline-flex items-center gap-1">Entry→Exit <MovementBadge delta={card.movement} /></span>
                    : <span>domain score</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── domain header shown at CG level ───────────────────────────────────────────
const DomainHeader: React.FC<{ card: DomainCard; studentTotal: number }> = ({ card, studentTotal }) => (
  <div className={`rounded-xl border border-gray-200 p-4 bg-gradient-to-br ${card.domain.soft}`}>
    <div className="flex items-center gap-4">
      <span className="text-2xl">{card.domain.icon}</span>
      <div className="flex-1">
        <p className="text-sm font-bold text-gray-800">{card.domain.label}</p>
        <p className="text-[13px] text-gray-700">{studentTotal.toLocaleString()} students · avg {card.current.avgPct}%</p>
      </div>
      <div className="w-40">
        <StackedBPrBar dist={card.current} height={10} showLabels />
      </div>
      {card.hasEntryExit && (
        <div className="w-36 pl-4 border-l border-white/60">
          <EntryExitBars entry={card.entry} exit={card.exit} compact />
        </div>
      )}
    </div>
  </div>
);

// ── loading placeholder while the aggregated analytics is in flight ───────────
const ExplorerLoading: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-700">
    <Loader2 size={26} className="animate-spin text-indigo-500" />
    <p className="text-sm font-medium">{label}</p>
  </div>
);

// ── info note for higher levels lacking rubric maps ───────────────────────────
const NoRubricNote: React.FC = () => (
  <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
    <Info size={15} className="text-amber-500 mt-0.5 shrink-0" />
    <p className="text-[13px] text-amber-700 leading-relaxed">
      You can browse every Curricular Goal and competency here. The <span className="font-semibold">Entry vs Exit distributions</span> are
      computed from per-student rubric records, available at <span className="font-semibold">class level</span> —
      drill into a class from the navigation below to populate the live numbers.
    </p>
  </div>
);
